import { Link, useParams } from 'react-router-dom';
import CourseCard from '@/components/base/CourseCard';
import { getCoursesByIds, getRelatedTopics, getTopicById } from '@/utils/course';

export default function Topic() {
  const { id } = useParams<{ id: string }>();
  const topic = id ? getTopicById(id) : undefined;

  if (!topic) {
    return (
      <div className="w-full px-4 md:px-8 py-20 flex flex-col items-center justify-center text-center">
        <span className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-4xl mb-5">
          <i className="ri-error-warning-line"></i>
        </span>
        <h1 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-2">
          专题不存在或已下架
        </h1>
        <p className="text-sm text-foreground-600 mb-6">该专题可能已被移除，去看看其他精选内容吧。</p>
        <Link
          to="/"
          className="h-11 px-6 inline-flex items-center rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          回到首页
        </Link>
      </div>
    );
  }

  const topicCourses = getCoursesByIds(topic.courseIds);
  const relatedTopics = getRelatedTopics(topic.id, 3);

  return (
    <div className="w-full">
      <section className="relative w-full h-[300px] md:h-[380px] overflow-hidden bg-primary-950">
        <img
          src={topic.cover}
          alt={topic.title}
          title={`${topic.title} · 秘密教学专题`}
          className="w-full h-full object-cover object-top opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/30"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-2xl text-background-50">
              <nav className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-background-100/70 mb-3">
                <Link to="/" className="hover:text-background-50 cursor-pointer">首页</Link>
                <span>/</span>
                <span>教育专题</span>
              </nav>
              <div className="flex flex-wrap gap-2 mb-3">
                {topic.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-md text-xs bg-accent-500 text-background-50 font-medium">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading font-extrabold text-2xl md:text-4xl leading-tight mb-3">
                {topic.title}
              </h1>
              <p className="text-sm md:text-base text-background-100/85 leading-relaxed max-w-xl">
                {topic.intro}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5 md:p-6 mb-10">
          <h2 className="font-heading font-bold text-base text-secondary-900 mb-2 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center text-secondary-600 text-lg">
              <i className="ri-lightbulb-line"></i>
            </span>
            推荐理由
          </h2>
          <p className="text-sm text-secondary-900/85 leading-relaxed">{topic.reason}</p>
        </div>

        <div className="mb-6">
          <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">
            专题包含课程
          </h2>
          <p className="text-sm text-foreground-600 mt-1.5">共 {topicCourses.length} 门课程，建议按顺序学习效果更佳。</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {topicCourses.map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>

        {relatedTopics.length > 0 && (
          <section className="mt-12 md:mt-16">
            <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-5">
              相关专题
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedTopics.map((t) => (
                <Link
                  key={t.id}
                  to={`/topics/${t.id}`}
                  className="group flex gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                >
                  <span className="w-24 h-20 rounded-md overflow-hidden bg-background-200 shrink-0">
                    <img src={t.cover} alt={t.title} className="w-full h-full object-cover object-top" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-heading font-bold text-sm text-foreground-950 mb-1 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {t.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">{t.intro}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}